import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Plus, Trash2, House, DollarSign, BarChart2 } from 'lucide-react';
import classNames from 'classnames';
import Select from '../../components/Select';
import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import TabsCut from '../../components/TabsCut';
import Actions from '../../components/Actions';
import Button from '../../components/Button';
import List from '../../components/List';
import Line from '../../components/Line';
import Text from '../../components/Text';
import Title from '../../components/Title';
import { COSTS_MAX_COUNT, SELECT_STYLES } from '../../utils/const';
import { COST_PRICE_OPTIONS } from '../../utils/factories/costPriceOptions';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import ConfirmationModal from '../../components/ConfirmationModal';
import NumberInput from '../../components/NumberInput';
import Textarea from '../../components/Textarea';
import MobileMenuHeader from '../../components/MobileMenuHeader';
import { selectValue } from '../../utils/helpers';
import TabsListTitle from '../../components/TabsListTitle';

const fields = ['name', 'amount', 'period'];

export default function FixedCost(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      common,
      tipCustomerSegment,
      caseStudies,
      cost: {
        name,
        fixedCost: { header, title, text, list, tabs },
      },
    },
  } = props;
  const { collection } = useSelector((state) => state.cost);
  const {
    cost: { addCost, updateCost, removeCost },
  } = useDispatch();
  const [opened, setOpened] = useState(false);

  const getTableHeaderIconByIndex = (index) => {
    // eslint-disable-next-line default-case
    switch (index) {
      case 0:
        return <House className="w-6 h-auto" />;
      case 1:
        return <DollarSign className="w-6 h-auto" />;
      case 2:
        return <BarChart2 className="w-6 h-auto" />;
    }
  };

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-cost"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--violet"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="FinanceCanvas" title={name} color="violet" />
        }
      />

      <Text align="center">{[text[0]]}</Text>

      <Title text={title[0]} align="left" />

      <List
        items={list}
        className="margin-top-40"
        icons={getTableHeaderIconByIndex}
      />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--violet">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[1]]}</Text>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60 bmc-page-tabs--violet"
      >
        {[
          <div key="strategy">
            <div>
              {collection.map((item, index) => (
                <TabsList key={item.id} divider={index < COSTS_MAX_COUNT - 1}>
                  <TabsListTitle className="flex flex-row justify-between items-center">
                    {locales.cost.fixedCostTitle} {index + 1}
                    {collection.length > 1 && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body={locales.common.deleteConfirmation}
                        confirmText="Delete"
                        buttonAriaLabel="Delete"
                        onConfirm={() => removeCost({ index, id: item.id })}
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )}
                  </TabsListTitle>

                  <TabsListItem
                    divider={false}
                    className="mb-5"
                    icon={getTableHeaderIconByIndex(0)}
                    title={tabs[fields[0]]}
                    text={
                      // eslint-disable-next-line
                      <Textarea
                        delay={700}
                        value={item.name || ''}
                        onChange={(event) =>
                          updateCost({
                            index,
                            name: 'name',
                            value: event.target.value,
                            id: item.id,
                          })
                        }
                        placeholder={tabs.namePlaceholder}
                      />
                    }
                  />

                  <TabsListItem
                    divider={false}
                    className="mb-5"
                    icon={getTableHeaderIconByIndex(1)}
                    title={tabs[fields[1]]}
                    text={
                      // eslint-disable-next-line
                      <NumberInput
                        rows={1}
                        value={
                          Array.isArray(item.amount)
                            ? item.amount[0] ?? ''
                            : item.amount ?? ''
                        }
                        onChange={(event) =>
                          updateCost({
                            index,
                            name: 'amount',
                            valueIndex: 0,
                            value: event.target.value,
                            id: item.id,
                          })
                        }
                        placeholder={tabs.amountPlaceholder}
                      />
                    }
                  />

                  <TabsListItem
                    divider={false}
                    className="mb-5"
                    icon={getTableHeaderIconByIndex(2)}
                    title={tabs[fields[2]]}
                    text={
                      // eslint-disable-next-line
                      <Select
                        styles={SELECT_STYLES}
                        className="text-black"
                        options={COST_PRICE_OPTIONS}
                        value={selectValue(item.period)}
                        placeholder={tabs.periodPlaceholder}
                        onChange={({ value }) =>
                          updateCost({
                            index,
                            name: 'period',
                            value,
                            id: item.id,
                          })
                        }
                      />
                    }
                  />
                </TabsList>
              ))}
            </div>

            {collection.length < COSTS_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions">
                <Button
                  variant="outline"
                  onClick={addCost}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewSomething}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  setOpened={setOpened}
                  opened={opened}
                  caseStudy={item}
                />

                <table className="w-full ">
                  <thead>
                    <tr>
                      {fields.map((field, i) => {
                        return (
                          <th
                            className={classNames('w-1/3', {
                              'px-4': i > 0 && i < fields.length - 1, // Padding left and right if not first or last
                            })}
                            key={field}
                          >
                            <div className="flex flex-row items-center mb-6">
                              <span className="p-2.5 bg-muted-lighter/10 rounded-md flex items-center justify-center mr-3">
                                {getTableHeaderIconByIndex(i)}
                              </span>
                              <span className="text-xl">{tabs[field]}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {item.costs.map((cost, i) => (
                      <tr key={i}>
                        {fields.map((field, fieldIndex) => (
                          <td
                            key={field}
                            className={classNames('text-start', {
                              'px-4':
                                fieldIndex > 0 &&
                                fieldIndex < fields.length - 1, // Padding left and right if not first or last
                            })}
                          >
                            <div className="mb-4">{cost[field]}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
